import { useStore } from 'Store';
import type { PlayerData } from 'Types';
import { isLiveTv, isRecording, isVod } from 'Utils';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Image } from 'react-native';

let lastFetch = null;

export const ThumbnailsContext = createContext({
    spritesheet: null,
    imagesBase64: {},
    item: null,
    setItem: (item: PlayerData['item'] | null) => { },
    refreshThumbnails: () => { }
});

export const ThumbnailsProvider = ({ children }) => {
    const [spritesheet, setSpritesheet] = useState(null);
    const [images, setImages] = useState({});
    const [lastImage, setLastImage] = useState(null);
    const [imagesBase64, setImagesBase64] = useState({});
    const [item, setItem] = useState<null | PlayerData['item']>(null);
    const { mCoreConfig } = useStore();

    const buildQuery = useCallback((endpoint: string, assetId: string) => {
        const query = `${mCoreConfig.thumbnailSpriteSheetsCdnBaseUrl + endpoint}`
            .replace('{assetId}', assetId);
        return query.replace(/([^:]\/)\/+/g, '$1');
    }, [mCoreConfig.thumbnailSpriteSheetsCdnBaseUrl]);

    const fetchSpriteSheetConfig = useCallback(async (asset) => {
        const recordingEndpoint = '/schedules/thumbnails/sprite-sheets?scheduleId={assetId}&service=DVR';
        const scheduleEndpoint = '/schedules/thumbnails/sprite-sheets?service=ARCHIVE&scheduleId={assetId}';
        const vodEndpoint = '/vod/movies/thumbnails/sprite-sheets?service=VOD&movieId={assetId}';

        const assetId = `${asset.getId()}`;

        let query = '';

        if (isLiveTv(asset)) {
            query = asset.getData().channelType === 'VOD'
                ? buildQuery(vodEndpoint, assetId)
                : buildQuery(scheduleEndpoint, assetId);
        } else if (isVod(asset)) {
            query = buildQuery(vodEndpoint, assetId);
        } else if (isRecording(asset)) {
            query = buildQuery(recordingEndpoint, assetId);
        }

        try {
            const response = await fetch(query);
            const json = await response.json();
            return json;
        } catch (e) {
            return null;
        }
    }, [buildQuery]);

    const fetchSpriteSheet = useCallback(async (asset) => {
        const assetId = `${asset?.getId()}`;

        if (!assetId) return null;
        if (isLiveTv(asset) && asset.getScheduleStatus() === 'future') return null;

        try {
            const config = await fetchSpriteSheetConfig(asset);
            if (!config?.success) {
                throw new Error(config?.errorMessage || 'Spritesheet not found');
            }

            const sheetUrl = config?.bigScreensSpriteSheetUrl;
            let model;
            if (sheetUrl) {
                const spritesheetResponse = await fetch(sheetUrl);
                model = await spritesheetResponse.json();
            }

            if (model) {
                return model;
            }
            return null;
        } catch (error) {
            return null;
        }
    }, [fetchSpriteSheetConfig]);

    const getBase64FromUrl = useCallback(async (url) => {
        const response = await fetch(url);
        if (response.status !== 200) {
            throw new Error('Failed to fetch image');
        }

        setLastImage(url);
        const blob = await response.blob();
        const reader = new FileReader();

        return new Promise((resolve) => {
            reader.onloadend = () => {
                resolve(reader.result);
            };
            reader.readAsDataURL(blob);
        });
    }, []);

    const fetchImages = useCallback(async (imagesObj, refresh = false) => {
        const i = {};
        const now = Date.now();
        const sec = ((now - item?.data?.start) - 70000) / 1000;

        if (Object.entries(imagesObj).length > 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const [key, image] of Object.entries(imagesObj)) {
                try {
                    if ((typeof imagesBase64[key] !== 'undefined' && key !== lastImage) && refresh) {
                        i[key] = imagesBase64[key];
                    } else if (sec > image.sec) {
                        // eslint-disable-next-line no-await-in-loop
                        const base64 = await getBase64FromUrl(key);
                        i[key] = base64;
                    }
                } catch (e) {
                    // silent
                }
            }
        }
        return i;
    }, [imagesBase64, lastImage, item, getBase64FromUrl]);

    const preloadImages = useCallback(async (asset: PlayerData['item']) => {
        try {
            const sprite = await fetchSpriteSheet(asset);
            if (sprite && sprite?.sprites && sprite?.sprites.length > 0) {
                const imagesObj = {};
                if (sprite?.sprites) {
                    sprite.sprites.forEach((element) => {
                        if (!imagesObj[sprite.baseUrl + element.file]) {
                            imagesObj[sprite.baseUrl + element.file] = {
                                url: sprite.baseUrl + element.file,
                                sec: element.sec,
                            };
                        }
                    });
                }
                const i = await fetchImages(imagesObj);
                setSpritesheet(sprite);
                setImages(imagesObj);
                setImagesBase64(i);
            }
        } catch (e) {
            console.log(e);
        }
    }, [fetchImages, fetchSpriteSheet]);

    const refreshThumbnails = useCallback(async () => {
        if (item?.data?.thumbnailsEnabled !== true || !item?.data?.start) return;
        const fetchTime = Date.now();
        if (fetchTime - lastFetch < 5000) {
            return;
        }
        lastFetch = fetchTime;
        if (images) {
            const i = await fetchImages(images, true);
            if (Object.keys(i).length > 0) {
                setImagesBase64(i);
            }
        }
    }, [item, images, fetchImages]);

    useEffect(() => {
        lastFetch = Date.now();
        setSpritesheet(null);
        setImagesBase64({});
        setImages({});
        setLastImage(null);
        if (item) {
            preloadImages(item);
        }
    }, [item]);

    return (
        <ThumbnailsContext.Provider
            value={{
                spritesheet,
                imagesBase64,
                setItem,
                item,
                refreshThumbnails
            }}
        >
            {children}
        </ThumbnailsContext.Provider>
    );
};

export const useThumbnails = () => useContext(ThumbnailsContext);

export const withThumbnails = (Component) => (props) =>
    <ThumbnailsContext.Consumer>
        {(thumbnail) => <Component {...props} {...thumbnail} />}
    </ThumbnailsContext.Consumer>;
